import { Injectable, effect, inject, signal } from '@angular/core';
import { MyAuthService, UserDto } from '../../../auth/auth.service';
import { ProjectService } from '../project/project.service';
import { UserService } from '../permissions-management/user.service';
import { AVAILABLE_LANGUAGES, I18nService } from '../../i18n/i18n.service';
import { AVAILABLE_THEMES, ThemeService } from '../theme.service';
import { ErrorService } from '../error/error.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly authService = inject(MyAuthService);
  private readonly projectService = inject(ProjectService);
  private readonly userService = inject(UserService);
  private readonly i18nService = inject(I18nService);
  private readonly themeService = inject(ThemeService);
  private readonly errorService = inject(ErrorService);

  currentUser = signal<UserDto | null>(null);

  constructor() {
    effect(() => {
      const ensuredUser = this.authService.ensuredUser();
      if (!ensuredUser) return;

      this.currentUser.set(ensuredUser);

      this.i18nService.lang.set(
        (ensuredUser.preferences.language as AVAILABLE_LANGUAGES) ||
          AVAILABLE_LANGUAGES.EN,
      );

      this.themeService.isDarkMode.set(
        ensuredUser.preferences.theme === AVAILABLE_THEMES.DARK,
      );
    });
  }

  logout(): void {
    this.projectService.closeProject(true);
    this.authService.logout();
  }

  updateUserTheme() {
    const user = this.currentUser();
    if (!user) {
      this.errorService.handle(`Could not find user to update theme`, {
        userMessage: 'Could not find user to update theme',
        context: {
          source: 'sessionService',
          action: 'updatingUserTheme',
        },
      });
      return;
    }

    firstValueFrom(
      this.userService.updateUserTheme(
        user._id,
        this.themeService.isDarkMode()
          ? AVAILABLE_THEMES.LIGHT
          : AVAILABLE_THEMES.DARK,
      ),
    )
      .then((updatedUser) => {
        if (updatedUser) {
          this.currentUser.set(updatedUser);
        }
        this.themeService.toggleDarkMode();
      })
      .catch((error) => {
        this.errorService.handle(
          `Could not update user theme to db; ${error}`,
          {
            userMessage: `Could not update user theme to the db ${error}`,
            context: {
              source: 'sessionService',
              action: 'updatingUserTheme',
            },
          },
        );
      });
  }

  updateUserLanguage(lang: string) {
    const user = this.currentUser();
    if (!user) {
      this.errorService.handle(`Could not find user to update language`, {
        userMessage: 'Could not find user to update language',
        context: {
          source: 'sessionService',
          action: 'updatingUserLanguage',
        },
      });
      return;
    }

    firstValueFrom(this.userService.updateUserLanguage(user._id, lang))
      .then((updatedUser) => {
        this.currentUser.set(updatedUser);
        this.i18nService.lang.set(lang as AVAILABLE_LANGUAGES);
      })
      .catch((error) => {
        this.errorService.handle(
          `Could not update user language to db; ${error}`,
          {
            userMessage: `Could not update user language to the db ${error}`,
            context: {
              source: 'sessionService',
              action: 'updatingUserLanguage',
            },
          },
        );
      });
  }
}
